import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { tasksAPI, contactsAPI, companiesAPI, dealsAPI, ticketsAPI, resourceDocumentsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, ListTodo, Calendar as CalendarIcon, Edit, Trash2, Upload, X, FileText, 
  User, Building, DollarSign, Ticket, ChevronRight, Link2, CheckSquare, 
  Headphones, Wrench, Briefcase, Layers, Eye, Bot, Clock, Download, Zap, Loader2
} from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { AssigneeSelect, AssigneeBadge } from '../components/AssigneeSelect';
import { AITaskBadge, AIProcessingIndicator } from '../components/AIProcessingIndicator';

// Task Types with hierarchy priority
const TASK_TYPES = [
  { value: 'technical_support', label: 'Technical Support', icon: Wrench, color: 'bg-red-100 text-red-700', priority: 4 },
  { value: 'general_support', label: 'General Support', icon: Headphones, color: 'bg-orange-100 text-orange-700', priority: 3 },
  { value: 'sales', label: 'Sales', icon: Briefcase, color: 'bg-blue-100 text-blue-700', priority: 2 },
  { value: 'crm', label: 'CRM', icon: Layers, color: 'bg-slate-100 text-slate-700', priority: 1 }
];

export const TasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // View task detail state (for deep linking)
  const [viewingTask, setViewingTask] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTab, setViewTab] = useState('details'); // details, ai-history, activity
  const [aiHistory, setAiHistory] = useState(null);
  const [loadingAiHistory, setLoadingAiHistory] = useState(false);
  
  // Related record cache for displaying names
  const [relatedCache, setRelatedCache] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    due_date: null,
    task_type: 'crm',
    progress: 0,
    checklist: [],
    attachments: [],
    related_to_type: '',
    related_to_id: '',
    assigned_to: ''
  });
  
  // New checklist item
  const [newChecklistItem, setNewChecklistItem] = useState('');

  useEffect(() => {
    fetchTasks();
  }, []);
  
  // Handle deep linking - open task from URL parameter
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setViewingTask(task);
        setViewDialogOpen(true);
        // Clear the URL parameter after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, tasks, setSearchParams]);

  const fetchTasks = async () => {
    try {
      const response = await tasksAPI.getAll();
      const tasksData = response.data || [];
      setTasks(tasksData);
      
      // Fetch related record names
      const uniqueRelations = {};
      tasksData.forEach(task => {
        if (task.related_to_type && task.related_to_id) {
          const key = `${task.related_to_type}_${task.related_to_id}`;
          uniqueRelations[key] = { type: task.related_to_type, id: task.related_to_id };
        }
      });
      
      // Fetch names for each related record
      const cache = {};
      for (const [key, relation] of Object.entries(uniqueRelations)) {
        try {
          let name = '';
          if (relation.type === 'contact') {
            const res = await contactsAPI.getById(relation.id);
            name = `${res.data.first_name} ${res.data.last_name}`;
          } else if (relation.type === 'company') {
            const res = await companiesAPI.getById(relation.id);
            name = res.data.name;
          } else if (relation.type === 'deal') {
            const res = await dealsAPI.getById(relation.id);
            name = res.data.title;
          } else if (relation.type === 'ticket') {
            const res = await ticketsAPI.getById(relation.id);
            name = res.data.subject;
          }
          cache[key] = name;
        } catch (e) {
          cache[key] = 'Unknown';
        }
      }
      setRelatedCache(cache);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch AI history for a task
  const fetchAiHistory = async (taskId) => {
    setLoadingAiHistory(true);
    try {
      const response = await tasksAPI.getAIHistory(taskId);
      setAiHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch AI history:', error);
      setAiHistory({ ai_logs: [], deliverables: [], activity_logs: [], has_ai_work: false });
    } finally {
      setLoadingAiHistory(false);
    }
  };
  
  // When viewing a task, also fetch AI history
  useEffect(() => {
    if (viewingTask && viewDialogOpen) {
      fetchAiHistory(viewingTask.id);
    }
  }, [viewingTask, viewDialogOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      due_date: formData.due_date?.toISOString() || null,
      related_to_type: formData.related_to_type || null,
      related_to_id: formData.related_to_id || null
    };
    
    try {
      if (editingTask) {
        await tasksAPI.update(editingTask.id, submitData);
        toast.success('Task updated');
      } else {
        await tasksAPI.create(submitData);
        toast.success('Task created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      due_date: task.due_date ? new Date(task.due_date) : null,
      task_type: task.task_type || 'crm',
      progress: task.progress || 0,
      checklist: task.checklist || [],
      attachments: task.attachments || [],
      related_to_type: task.related_to_type || '',
      related_to_id: task.related_to_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksAPI.delete(id);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleToggleComplete = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    const newProgress = newStatus === 'completed' ? 100 : task.progress || 0;
    try {
      await tasksAPI.update(task.id, { status: newStatus, progress: newProgress });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      due_date: null,
      task_type: 'crm',
      progress: 0,
      checklist: [],
      attachments: [],
      related_to_type: '',
      related_to_id: ''
    });
    setNewChecklistItem('');
  };

  // Checklist functions
  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    const newItem = {
      id: `item_${Date.now()}`,
      text: newChecklistItem.trim(),
      completed: false
    };
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, newItem]
    }));
    setNewChecklistItem('');
  };

  const toggleChecklistItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map(item => 
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  const removeChecklistItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter(item => item.id !== itemId)
    }));
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      const response = await resourceDocumentsAPI.uploadFile(file);
      handleChange('attachments', [...formData.attachments, response.data.file_url]);
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (url) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a !== url)
    }));
  };

  // Calculate progress from checklist
  const calculateProgress = () => {
    if (formData.checklist.length === 0) return formData.progress;
    const completed = formData.checklist.filter(item => item.completed).length;
    return Math.round((completed / formData.checklist.length) * 100);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesType = typeFilter === 'all' || task.task_type === typeFilter;
    const matchesAssignee = assigneeFilter === 'all' || task.assigned_to === assigneeFilter;
    return matchesStatus && matchesType && matchesAssignee;
  });

  // Sort by task type priority (higher priority first), then by due date
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Completed tasks at the bottom
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    
    // Then sort by task type priority (higher first)
    const aTypePriority = TASK_TYPES.find(t => t.value === a.task_type)?.priority || 0;
    const bTypePriority = TASK_TYPES.find(t => t.value === b.task_type)?.priority || 0;
    if (aTypePriority !== bTypePriority) return bTypePriority - aTypePriority;
    
    // Then sort by due date
    if (a.due_date && b.due_date) {
      return new Date(a.due_date) - new Date(b.due_date);
    }
    return 0;
  });

  const priorityColors = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-red-100 text-red-700'
  };

  const getDateColor = (date) => {
    if (!date) return 'text-muted-foreground';
    const d = new Date(date);
    if (isPast(d) && !isToday(d)) return 'text-red-600';
    if (isToday(d)) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  const getTaskTypeInfo = (type) => {
    return TASK_TYPES.find(t => t.value === type) || TASK_TYPES[3];
  };

  const getRelatedIcon = (type) => {
    switch (type) {
      case 'contact': return User;
      case 'company': return Building;
      case 'deal': return DollarSign;
      case 'ticket': return Ticket;
      case 'memo': return FileText;
      case 'ai_agent': return Bot;
      default: return Link2;
    }
  };

  const getRelatedPath = (type, id) => {
    switch (type) {
      case 'contact': return `/contacts/${id}`;
      case 'company': return `/companies/${id}`;
      case 'deal': return `/deals/${id}`;
      case 'ticket': return `/tickets/${id}`;
      case 'memo': return `/memos`;
      case 'ai_agent': return `/ai-workforce`;
      default: return '#';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Tasks</h1>
          <p className="text-muted-foreground mt-1">Manage and track your work</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Edit Task' : 'Create Task'}</DialogTitle>
              <DialogDescription>
                {editingTask ? 'Update task details' : 'Create a new task with checklist and attachments'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                    data-testid="task-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Task Type</Label>
                  <Select value={formData.task_type} onValueChange={(v) => handleChange('task_type', v)}>
                    <SelectTrigger data-testid="task-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                    <SelectTrigger data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.due_date ? format(formData.due_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.due_date}
                        onSelect={(date) => handleChange('due_date', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label>Assign To</Label>
                  <AssigneeSelect
                    value={formData.assigned_to}
                    onValueChange={(v) => handleChange('assigned_to', v === 'unassigned' ? '' : v)}
                    placeholder="Select team member or AI agent"
                    includeAI={true}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Related Record */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Related To (Type)</Label>
                  <Select 
                    value={formData.related_to_type || 'none'} 
                    onValueChange={(v) => handleChange('related_to_type', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="contact">Contact</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                      <SelectItem value="deal">Deal</SelectItem>
                      <SelectItem value="ticket">Ticket</SelectItem>
                      <SelectItem value="memo">Memo/Note</SelectItem>
                      <SelectItem value="ai_agent">AI Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Related Record ID</Label>
                  <Input
                    value={formData.related_to_id}
                    onChange={(e) => handleChange('related_to_id', e.target.value)}
                    placeholder="Enter record ID"
                    disabled={!formData.related_to_type}
                    data-testid="task-related-id-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="description">Description</Label>
                  <span className={`text-xs ${formData.description.length > 1800 ? 'text-red-500' : 'text-muted-foreground'}`}>
                    {formData.description.length}/2000
                  </span>
                </div>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 2000) {
                      handleChange('description', e.target.value);
                    }
                  }}
                  rows={3}
                  data-testid="task-description-input"
                  maxLength={2000}
                />
                {formData.description.length > 1800 && (
                  <p className="text-xs text-amber-600">Approaching character limit</p>
                )}
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Progress: {calculateProgress()}%</Label>
                  {formData.checklist.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {formData.checklist.filter(i => i.completed).length}/{formData.checklist.length} items
                    </span>
                  )}
                </div>
                <Progress value={calculateProgress()} className="h-2" />
                {formData.checklist.length === 0 && (
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.progress}
                    onChange={(e) => handleChange('progress', parseInt(e.target.value))}
                    className="mt-2"
                  />
                )}
              </div>

              {/* Checklist */}
              <div className="space-y-2">
                <Label>Checklist Items</Label>
                <div className="flex gap-2">
                  <Input
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    placeholder="Add checklist item..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                    data-testid="checklist-input"
                  />
                  <Button type="button" variant="outline" onClick={addChecklistItem}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.checklist.length > 0 && (
                  <ScrollArea className="h-32 border rounded-md p-2">
                    {formData.checklist.map(item => (
                      <div key={item.id} className="flex items-center gap-2 py-1">
                        <Checkbox
                          checked={item.completed}
                          onCheckedChange={() => toggleChecklistItem(item.id)}
                        />
                        <span className={`flex-1 text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                          {item.text}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChecklistItem(item.id)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div 
                  className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm">Click to upload (max 10MB)</p>
                  {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                {formData.attachments.length > 0 && (
                  <div className="space-y-1">
                    {formData.attachments.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="flex-1 text-sm truncate">{url.split('/').pop()}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(url)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="submit" disabled={uploading} data-testid="task-submit-btn">
                  {editingTask ? 'Save Changes' : 'Create Task'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="task-filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="task-filter-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TASK_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      {type.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <AssigneeSelect
              value={assigneeFilter}
              onValueChange={setAssigneeFilter}
              placeholder="All Assignees"
              includeUnassigned={false}
              includeAI={true}
              className="w-[200px]"
            />

            <div className="flex-1" />
            
            <div className="text-sm text-muted-foreground">
              {sortedTasks.length} task{sortedTasks.length !== 1 ? 's' : ''}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <div className="space-y-3">
        {sortedTasks.length > 0 ? (
          sortedTasks.map((task) => {
            const typeInfo = getTaskTypeInfo(task.task_type);
            const TypeIcon = typeInfo.icon;
            const RelatedIcon = getRelatedIcon(task.related_to_type);
            const relatedKey = `${task.related_to_type}_${task.related_to_id}`;
            const relatedName = relatedCache[relatedKey];
            const checklistProgress = task.checklist?.length > 0 
              ? Math.round((task.checklist.filter(i => i.completed).length / task.checklist.length) * 100)
              : task.progress || 0;

            return (
              <Card 
                key={task.id} 
                className={`${task.status === 'completed' ? 'opacity-60' : ''} cursor-pointer hover:shadow-md transition-shadow`} 
                data-testid={`task-card-${task.id}`}
                onClick={() => {
                  setViewingTask(task);
                  setViewDialogOpen(true);
                }}
              >
                <CardContent className="pt-4">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={task.status === 'completed'}
                      onCheckedChange={() => handleToggleComplete(task)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                      data-testid={`task-checkbox-${task.id}`}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </h3>
                        <Badge className={typeInfo.color}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {typeInfo.label}
                        </Badge>
                        <Badge className={priorityColors[task.priority]}>
                          {task.priority}
                        </Badge>
                      </div>
                      
                      {task.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        {task.due_date && (
                          <span className={`text-sm flex items-center gap-1 ${getDateColor(task.due_date)}`}>
                            <CalendarIcon className="h-4 w-4" />
                            {format(new Date(task.due_date), 'MMM d, yyyy')}
                          </span>
                        )}
                        
                        {/* Related Record Link */}
                        {task.related_to_type && task.related_to_id && (
                          <Link 
                            to={getRelatedPath(task.related_to_type, task.related_to_id)}
                            className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                            data-testid={`task-related-link-${task.id}`}
                          >
                            <RelatedIcon className="h-4 w-4" />
                            {relatedName || task.related_to_id}
                            <ChevronRight className="h-3 w-3" />
                          </Link>
                        )}
                        
                        {/* Checklist count */}
                        {task.checklist?.length > 0 && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <CheckSquare className="h-4 w-4" />
                            {task.checklist.filter(i => i.completed).length}/{task.checklist.length}
                          </span>
                        )}
                        
                        {/* Attachments count */}
                        {task.attachments?.length > 0 && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            {task.attachments.length} file{task.attachments.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        
                        {/* Assignee - with AI Processing Status */}
                        {task.assigned_to && (
                          <span className="text-sm flex items-center gap-1">
                            {task.assigned_to_type === 'ai_agent' ? (
                              <AITaskBadge task={task} />
                            ) : task.assigned_to.startsWith('ai_') ? (
                              <AITaskBadge task={task} />
                            ) : (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <User className="h-3 w-3 mr-1" />
                                Assigned
                              </Badge>
                            )}
                          </span>
                        )}
                      </div>
                      
                      {/* Progress bar */}
                      {checklistProgress > 0 && task.status !== 'completed' && (
                        <div className="mt-2">
                          <Progress value={checklistProgress} className="h-1.5" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setViewingTask(task);
                          setViewDialogOpen(true);
                        }}
                        data-testid={`view-task-${task.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(task)}
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(task.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <ListTodo className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-lg">No tasks found</h3>
              <p className="text-muted-foreground mt-1">Create your first task to get started</p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* View Task Detail Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Task Details
            </DialogTitle>
          </DialogHeader>
          {viewingTask && (
            <div className="space-y-4 py-4">
              {/* Task Title and Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{viewingTask.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={
                      viewingTask.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      viewingTask.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      viewingTask.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {viewingTask.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className={
                      viewingTask.priority === 'urgent' ? 'border-red-300 text-red-700' :
                      viewingTask.priority === 'high' ? 'border-orange-300 text-orange-700' :
                      viewingTask.priority === 'medium' ? 'border-amber-300 text-amber-700' :
                      'border-slate-300 text-slate-700'
                    }>
                      {viewingTask.priority?.toUpperCase()} Priority
                    </Badge>
                    {viewingTask.task_type && (
                      <Badge variant="outline">
                        {TASK_TYPES.find(t => t.value === viewingTask.task_type)?.label || viewingTask.task_type}
                      </Badge>
                    )}
                    {viewingTask.assigned_to_type === 'ai_agent' && (
                      <Badge className="bg-purple-100 text-purple-700">
                        <Bot className="h-3 w-3 mr-1" />
                        AI Assigned
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* AI Processing Status - Show for AI tasks */}
              {viewingTask.assigned_to_type === 'ai_agent' && (
                <div className="mb-4">
                  <AIProcessingIndicator 
                    taskId={viewingTask.id} 
                    onComplete={(status) => {
                      if (status === 'completed') {
                        // Refresh AI history when complete
                        fetchAiHistory(viewingTask.id);
                        toast.success('AI processing complete! Deliverable ready.');
                      }
                    }}
                  />
                </div>
              )}
              
              {/* Tabs for Details / AI History / Activity */}
              <Tabs value={viewTab} onValueChange={setViewTab} className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                  <TabsTrigger value="ai-history" className="flex-1">
                    <Bot className="h-3 w-3 mr-1" />
                    AI Work {aiHistory?.has_ai_work && <span className="ml-1 h-2 w-2 bg-purple-500 rounded-full" />}
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="flex-1">Activity</TabsTrigger>
                </TabsList>
                
                {/* Details Tab */}
                <TabsContent value="details" className="mt-4">
                  {/* Description */}
                  {viewingTask.description && (
                    <div className="bg-slate-50 p-3 rounded-lg mb-4">
                      <p className="text-sm whitespace-pre-wrap">{viewingTask.description}</p>
                    </div>
                  )}
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Due Date:</span>
                  <p className="font-medium">
                    {viewingTask.due_date ? format(new Date(viewingTask.due_date), 'MMM d, yyyy') : 'No due date'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span>
                  <p className="font-medium">
                    {viewingTask.created_at ? format(new Date(viewingTask.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
                {viewingTask.assigned_to_name && (
                  <div>
                    <span className="text-muted-foreground">Assigned To:</span>
                    <p className="font-medium flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {viewingTask.assigned_to_name}
                    </p>
                  </div>
                )}
                {viewingTask.related_to_type && viewingTask.related_to_id && (
                  <div>
                    <span className="text-muted-foreground">Related To:</span>
                    <p className="font-medium flex items-center gap-1">
                      {viewingTask.related_to_type === 'contact' && <User className="h-3 w-3" />}
                      {viewingTask.related_to_type === 'company' && <Building className="h-3 w-3" />}
                      {viewingTask.related_to_type === 'deal' && <DollarSign className="h-3 w-3" />}
                      {viewingTask.related_to_type === 'ticket' && <Ticket className="h-3 w-3" />}
                      {relatedCache[`${viewingTask.related_to_type}_${viewingTask.related_to_id}`] || viewingTask.related_to_name || 'View Record'}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Checklist */}
              {viewingTask.checklist && viewingTask.checklist.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Checklist</h4>
                  <div className="space-y-1">
                    {viewingTask.checklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={item.completed} disabled />
                        <span className={item.completed ? 'line-through text-muted-foreground' : ''}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attachments */}
              {viewingTask.attachments && viewingTask.attachments.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Attachments</h4>
                  <div className="flex flex-wrap gap-2">
                    {viewingTask.attachments.map((attachment, idx) => (
                      <a 
                        key={idx} 
                        href={typeof attachment === 'string' ? attachment : attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 border rounded-md text-sm hover:bg-slate-100"
                      >
                        <FileText className="h-3 w-3" />
                        {typeof attachment === 'string' 
                          ? attachment.split('/').pop() 
                          : attachment.filename || attachment.name || `Attachment ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}
                </TabsContent>
                
                {/* AI Work Tab */}
                <TabsContent value="ai-history" className="mt-4">
                  {loadingAiHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                    </div>
                  ) : aiHistory?.has_ai_work ? (
                    <div className="space-y-4">
                      {/* AI Deliverables */}
                      {aiHistory.deliverables && aiHistory.deliverables.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Bot className="h-4 w-4 text-purple-500" />
                            AI Deliverables
                          </h4>
                          <div className="space-y-2">
                            {aiHistory.deliverables.map((deliverable, idx) => (
                              <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="font-medium text-sm">{deliverable.title || deliverable.type || 'Deliverable'}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {deliverable.created_at ? format(new Date(deliverable.created_at), 'MMM d, yyyy h:mm a') : ''}
                                    </p>
                                  </div>
                                  {deliverable.file_url && (
                                    <a 
                                      href={deliverable.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-purple-600 hover:text-purple-800"
                                    >
                                      <Download className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                                {deliverable.content && (
                                  <div className="mt-2 p-2 bg-white rounded text-sm max-h-40 overflow-y-auto">
                                    <pre className="whitespace-pre-wrap text-xs">{deliverable.content.substring(0, 500)}{deliverable.content.length > 500 ? '...' : ''}</pre>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* AI Execution Logs */}
                      {aiHistory.ai_logs && aiHistory.ai_logs.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Zap className="h-4 w-4 text-amber-500" />
                            AI Processing History
                          </h4>
                          <ScrollArea className="h-[300px]">
                            <div className="space-y-2 pr-2">
                              {aiHistory.ai_logs.map((log, idx) => (
                                <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                                      {log.agent_name || 'AI Agent'}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {log.completed_at ? format(new Date(log.completed_at), 'MMM d, h:mm a') : ''}
                                    </span>
                                  </div>
                                  {log.brain_stem_output && (
                                    <div className="p-2 bg-white rounded text-xs max-h-32 overflow-y-auto">
                                      <pre className="whitespace-pre-wrap">{log.brain_stem_output.substring(0, 400)}{log.brain_stem_output.length > 400 ? '...' : ''}</pre>
                                    </div>
                                  )}
                                  {log.success === false && log.error_message && (
                                    <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                                      Error: {log.error_message}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Bot className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No AI work history for this task</p>
                      {viewingTask.assigned_to_type !== 'ai_agent' && (
                        <p className="text-xs mt-1">Assign this task to an AI agent to see AI deliverables here</p>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                {/* Activity Tab */}
                <TabsContent value="activity" className="mt-4">
                  {loadingAiHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : aiHistory?.activity_logs && aiHistory.activity_logs.length > 0 ? (
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2 pr-2">
                        {aiHistory.activity_logs.map((log, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2 border-l-2 border-slate-200">
                            <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <Clock className="h-3 w-3 text-slate-500" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm">{log.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {log.created_at ? format(new Date(log.created_at), 'MMM d, yyyy h:mm a') : ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No activity recorded yet</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setViewDialogOpen(false);
              if (viewingTask) handleEdit(viewingTask);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
